import BaseModel from '../base';
import Estimate from '../estimate/index';
import Question from '../question/index';
import Answer from '../answer/index';
import Task from '../task/index';

import { rServices } from '@/plugins/api';
import { localeDate } from '@/plugins/date-print';
import { ru } from 'date-fns/locale';

import differenceInSeconds from 'date-fns/differenceInSeconds';
import differenceInDays from 'date-fns/differenceInDays';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import upperFirst from 'lodash/upperFirst';
import { rHandler } from '@/plugins/RHandler';
import { apiService } from '@/services/APIService';
import i18n from '@/plugins/I18n';
import { I18N_KEY } from '@/constants/I18n';
import Expertise from '../expertise/index';

const EXPERTISE_EXPIRE_WARNING_DAYS = 10;
const ADDITIONAL_ATTEMPT_STATUS_NEW = 1;
const ADDITIONAL_ATTEMPT_ISSUER_EVENT_OWNER = 2;
const JOURNAL_API_PATH = 'event-poll-journals';

export default class Journal extends BaseModel {
  constructor(journal, url) {
    super(journal, url);

    this.id = journal.id;

    this.score = Number(journal.score || 0);
    this.score_100 = Number(journal.score_100 || 0);
    this.max_score = Number(journal.max_score || 0);
    this.experts_score = Number(journal.experts_score || 0);
    this.experts_score_100 = Number(journal.experts_score_100 || 0);
    this.max_experts_score = Number(journal.max_experts_score || 0);

    this.is_confirm = journal.is_confirm;
    this.is_checked = journal.is_checked;
    this.event_poll_id = journal.event_poll_id;
    this.poll_id = journal.poll_id;
    this.owner_id = journal.owner_id;
    this.event_stage_id = journal.event_stage_id;
    this.finished_at = journal.finished_at;
    this.attempt = journal.attempt;
    this.final_at = journal.final_at;
    this.team_id = journal.team_id;
    this.group_id = journal.group_id;

    this.title = (journal.title && upperFirst(journal.title)) || null;
    this.anonymous = journal.anonymous ?? true;
    this.owner = journal?.owner || null;
    this.owner_id = journal?.owner_id;
    this.owner_name = journal?.owner_name;
    this.own_score = journal?.own_score || null;
    this.not_own_expertises_count = journal?.not_own_expertises_count || 0;
    this.has_own_completed_expertise = journal?.has_own_completed_expertise || null;
    this.has_all_answers = journal?.has_all_answers || null;
    this.user_event_info_values = journal?.user_event_info_values || null;
    this.user_directory_documents = journal?.user_directory_documents || null;

    this.avg_expertises_score = journal.avg_expertises_score;
    this.sum = journal.sum;

    this.questions = (journal.questions || [])
      .map((item) => new Question(item, 'question'));
    this.answers = (journal.answers || [])
      .map((answer) => new Answer(answer, 'answer'));
    this.expertise = (journal.expertise || [])
      .map((expertise) => new Estimate(expertise, 'expertise'));
    this.my_expertise = journal.my_expertise ? new Estimate(journal.my_expertise) : null;
    this.poll = journal.poll ? new Task(journal.poll) : null;

    this.expertises = (journal.expertises || [])
      .map((expertise) => new Expertise(expertise, 'expertise'));

    this.dop_attempts = journal.dop_attempts || [];

    this.recalculate_100 = journal.recalculate_100 ?? false;
  }

  get expires() {
    const { final_at } = this;

    if (!final_at) return null;

    const expires = differenceInSeconds(
      localeDate(final_at),
      new Date(),
    );

    return expires > 0 ? expires : 0;
  }

  /**
   * Истек ли срок действия прохождения
   * @return boolean
   */
  get isExpired() {
    const expiresValue = !Number.isInteger(this.expires) || !!this.expires;
    return !expiresValue && this.final_at;
  }

  /**
   * Завершено ли выполнение заданий
   * @return boolean
   */
  get isFinished() {
    return !!this.finished_at;
  }

  get journalTitle() {
    return this.title || `#${this.id}`;
  }

  get dateOfWaitingEstimate() {
    const formatedStartDate = localeDate(new Date(this.finished_at));
    const timePassed = formatDistanceToNow(formatedStartDate, {
      locale: ru,
    });
    return timePassed;
  }

  get maxEstimateCount() {
    return this.poll?.settings?.max_estimate || 1;
  }

  get otherEstimateCount() {
    const { my_expertise } = this;
    return this.expertise
      .filter((i) => i.expert_id !== my_expertise?.expert_id)
      .length;
  }

  get isExpireExpertiseDate() {
    const expertiseEndAt = this.poll?.settings?.expertise_end_at;

    if (!expertiseEndAt) {
      return false;
    }

    const endDate = localeDate(expertiseEndAt);
    const now = new Date();
    const daysRemaining = differenceInDays(endDate, now);

    return daysRemaining <= EXPERTISE_EXPIRE_WARNING_DAYS;
  }

  get additionalAttempt() {
    const [item] = this.dop_attempts || [];
    return item;
  }

  get isExpertsScores() {
    const { max_experts_score } = this;
    return !!max_experts_score;
  }

  get correctScoresData() {
    const {
      isExpertsScores,
      score,
      score_100,
      max_score,
      experts_score,
      experts_score_100,
      max_experts_score,
    } = this;
    return {
      current: isExpertsScores ? experts_score : score,
      max: isExpertsScores ? max_experts_score : max_score,
      hundred: isExpertsScores ? experts_score_100 : score_100,
    };
  }

  get actualScore() {
    const { correctScoresData: { current, hundred }, recalculate_100 } = this;
    return recalculate_100 ? hundred : current;
  }

  get actualScoreWithMax() {
    const { recalculate_100, actualScore, correctScoresData: { max, current } } = this;
    return [
      actualScore,
      ...(recalculate_100 && (max || current)) ? [
        `(${current}`,
        'из',
        `${max})`,
      ] : [],
    ].join(' ');
  }

  get actualScoreWithMaxText() {
    const { actualScore, actualScoreWithMax } = this;
    return `${actualScoreWithMax} ${i18n.tc(`${I18N_KEY.point}.score.basic`, actualScore)}`;
  }

  /**
   * Завершить проходение заданий
   */
  finish(payload) {
    const args = [this.id];

    return rServices.polls.answers.finishPoll({ args, data: payload });
  }

  getAddAttemptUnavailableReason(task, now = new Date()) {
    if (!task.settings.can_add_attempts) return 'В настройках задания запрещены дополнительные попытки';
    return this.getReworkUnavailableReason(task, now);
  }

  getReworkUnavailableReason(task, now = new Date()) {
    const { settings } = task;
    if (
      (settings.available_start_at && localeDate(settings.available_start_at) > now)
      || (settings.available_end_at && localeDate(settings.available_end_at) < now)
    ) return 'Задание недоступно для прохождения';
    if (!this.finished_at && (!this.final_at || localeDate(this.final_at) > now)) {
      return 'Участник ещё не завершил текущую попытку';
    }
    if (this.attempt < settings.attempts) return 'У участника ещё остались основные попытки';
    if (this.dop_attempts.some(({ status, issuer_type }) => (
      Number(status) === ADDITIONAL_ATTEMPT_STATUS_NEW
      && (Number(issuer_type) === ADDITIONAL_ATTEMPT_ISSUER_EVENT_OWNER || settings.can_add_attempts)
    ))) {
      return 'У участника уже есть неиспользованная дополнительная попытка';
    }
    return '';
  }

  addAttempt(payload = {}) {
    return rHandler(
      apiService.post(`${JOURNAL_API_PATH}/${this.id}/add-attempt`, payload),
      (response) => ({ response }),
      (error) => ({ error }),
    );
  }
}
